import { notificationsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import type {
  ApiEnvelope,
  BackendInboxListResponse,
  BackendNotificationListResponse,
  BackendUnreadCountResponse,
  BulkUpsertPreferencesPayload,
  ChannelConfigResponse,
  ChannelTestPayload,
  Audience,
  ChannelTestResult,
  ChannelType,
  ConfigurationResponse,
  InboxResponse,
  PreferenceResponse,
  UpsertChannelConfigPayload,
  UpsertPreferencePayload,
} from '~/features/notifications/types/notifications.api.types';
import {
  toInboxNotification,
  toInboxNotificationFromEntry,
} from '~/features/notifications/types/notifications.api.types';

// ---------------------------------------------------------------------------
// Channel configs (admin)
// ---------------------------------------------------------------------------

/** GET /admin/channel-config — list every channel-config row for the tenant. */
export async function fetchChannelConfigs(): Promise<ChannelConfigResponse[]> {
  const res = await notificationsClient
    .get(endpoints.notifications.channelConfigs())
    .json<ApiEnvelope<ChannelConfigResponse[]>>();
  return res.data;
}

/** PUT /admin/channel-config/{channel_type} — upsert one channel's credentials/config. */
export async function upsertChannelConfig(
  channelType: ChannelType,
  payload: UpsertChannelConfigPayload,
): Promise<ChannelConfigResponse> {
  const res = await notificationsClient
    .put(endpoints.notifications.channelConfigByType(channelType), { json: payload })
    .json<ApiEnvelope<ChannelConfigResponse>>();
  return res.data;
}

/** POST /admin/channel-config/{channel_type}/test — fire a test message without persisting a notification. */
export async function testChannelConfig(
  channelType: ChannelType,
  payload: ChannelTestPayload,
): Promise<ChannelTestResult> {
  const res = await notificationsClient
    .post(endpoints.notifications.channelConfigTest(channelType), { json: payload })
    .json<ApiEnvelope<ChannelTestResult>>();
  return res.data;
}

// ---------------------------------------------------------------------------
// Configurations (alert topics)
// ---------------------------------------------------------------------------

/**
 * GET /admin/configurations — list alert topic configurations.
 *
 * Pass an `audience` to scope to customer-facing or internal/ops topics; omit
 * for all configurations (existing behaviour).
 */
export async function fetchConfigurations(
  params: { audience?: Audience } = {},
): Promise<ConfigurationResponse[]> {
  const res = await notificationsClient
    .get(endpoints.notifications.configurations(), {
      ...(params.audience ? { searchParams: { audience: params.audience } } : {}),
    })
    .json<ApiEnvelope<ConfigurationResponse[]>>();
  return res.data;
}

// ---------------------------------------------------------------------------
// Preferences (per-configuration × per-channel toggle state + delivery target)
// ---------------------------------------------------------------------------

/** GET /preferences — list the current user's preference rows. */
export async function fetchPreferences(): Promise<PreferenceResponse[]> {
  const res = await notificationsClient
    .get(endpoints.notifications.preferences())
    .json<ApiEnvelope<PreferenceResponse[]>>();
  return res.data;
}

/** POST /preferences/seed — idempotently create default-disabled rows for every (config × channel). */
export async function seedPreferences(): Promise<{ count: number }> {
  const res = await notificationsClient
    .post(endpoints.notifications.preferencesSeed())
    .json<ApiEnvelope<{ count: number }>>();
  return res.data;
}

/** PUT /preferences — upsert a preference by (configuration_id, channel_type). */
export async function upsertPreference(
  payload: UpsertPreferencePayload,
): Promise<PreferenceResponse> {
  const res = await notificationsClient
    .put(endpoints.notifications.preferences(), { json: payload })
    .json<ApiEnvelope<PreferenceResponse>>();
  return res.data;
}

/**
 * PUT /preferences/bulk — apply one channel target+status to multiple
 * configuration IDs in a single request. Replaces N parallel upsert calls
 * from the Delivery Channels screen with one atomic round-trip.
 */
export async function bulkUpsertPreferences(
  payload: BulkUpsertPreferencesPayload,
): Promise<PreferenceResponse[]> {
  const res = await notificationsClient
    .put(endpoints.notifications.preferencesBulk(), { json: payload })
    .json<ApiEnvelope<PreferenceResponse[]>>();
  return res.data;
}

// ---------------------------------------------------------------------------
// Inbox
// ---------------------------------------------------------------------------

/** POST /notifications/inbox/list — paginated inbox for the current user. */
export async function fetchInbox(page = 1, size = 20): Promise<InboxResponse> {
  const res = await notificationsClient
    .post(endpoints.notifications.inbox(), {
      json: { pagination: { page, size }, filters: {} },
    })
    .json<BackendInboxListResponse>();
  const notifications = res.data.map(toInboxNotification);
  return {
    notifications,
    unread_count: notifications.filter((n) => !n.is_read).length,
    pagination: {
      total: res.pagination.total,
      page: res.pagination.page,
      size: res.pagination.size,
      pages: res.pagination.totalPages,
    },
  };
}

/** GET /notifications/inbox/unread-count — live unread count for the bell badge. */
export async function fetchUnreadCount(): Promise<number> {
  const res = await notificationsClient
    .get(endpoints.notifications.inboxUnreadCount())
    .json<BackendUnreadCountResponse>();
  return res.data.count;
}

/** POST /notifications/read-all — bulk mark every unread notification as read. */
export async function markAllInboxRead(): Promise<void> {
  await notificationsClient.post(endpoints.notifications.inboxMarkAllRead());
}

/** POST /notifications/{id}/read — mark a single notification as read (idempotent). */
export async function markNotificationRead(id: string): Promise<void> {
  await notificationsClient.post(endpoints.notifications.markNotificationRead(id));
}

/** POST /notifications/list — full notifications list (for the /notification page). */
export async function fetchAllNotifications(page = 1, size = 50): Promise<InboxResponse> {
  const res = await notificationsClient
    .post(endpoints.notifications.allNotifications(), {
      json: { pagination: { page, size }, filters: {} },
    })
    .json<BackendNotificationListResponse>();
  const notifications = res.data.map(toInboxNotificationFromEntry);
  return {
    notifications,
    // /notifications/list is a dispatch log, not a per-user inbox —
    // read state is not tracked at this level.
    unread_count: 0,
    pagination: {
      total: res.pagination.total,
      page: res.pagination.page,
      size: res.pagination.size,
      pages: res.pagination.totalPages,
    },
  };
}
